/**
 * CommandActionGrid — uniform grid of action buttons for remote commands,
 * quick actions, or operator tools.
 *
 * Use when: rendering FERNAKTIONEN (Soft Reset / Hard Reset / Unlock / …)
 * in Station Diagnostics, quick-action drawers in Triage, or any 2- or
 * 3-column button cluster. Each action has a label, icon, optional intent
 * (`primary` | `danger`), tooltip, and disabled state.
 */
import type { ReactElement } from 'react';
import { Button, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

export interface CommandAction {
  key: string;
  label: string;
  icon: ReactElement;
  /** "primary" highlights, "danger" uses red tone via Fluent. Otherwise outline. */
  intent?: 'default' | 'primary' | 'danger';
  /** Disable + reason via tooltip. */
  disabled?: boolean;
  /** Tooltip content (always rendered when present). */
  tooltip?: string;
  onClick?: () => void;
}

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gap: tokens.spacingHorizontalS,
  },
  cols2: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  cols3: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
  cols4: { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
  // `columns="auto"` — a content-width button row that flows side by side and
  // wraps to the next line only when it runs out of horizontal room. Use when
  // the actions are few and shouldn't stretch to full width (e.g. the L1
  // Quick-Fix console).
  flow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  btn: {
    justifyContent: 'flex-start',
    width: '100%',
  },
  // Flow buttons size to their label (no forced full width) so they sit next to
  // each other; the icon + label stay centred.
  flowBtn: {
    justifyContent: 'center',
    flex: '0 0 auto',
  },
  danger: {
    color: tokens.colorPaletteRedForeground1,
  },
});

interface CommandActionGridProps {
  actions: CommandAction[];
  /** Fixed column count, or `"auto"` for a content-width wrapping row. */
  columns?: 2 | 3 | 4 | 'auto';
}

export function CommandActionGrid({ actions, columns = 2 }: CommandActionGridProps) {
  const styles = useStyles();
  const isFlow = columns === 'auto';
  const containerClass = isFlow
    ? styles.flow
    : mergeClasses(
        styles.grid,
        columns === 4 ? styles.cols4 : columns === 3 ? styles.cols3 : styles.cols2,
      );

  return (
    <div className={containerClass}>
      {actions.map((a) => {
        const appearance = a.intent === 'primary' ? 'primary' : 'outline';
        const className = mergeClasses(
          isFlow ? styles.flowBtn : styles.btn,
          a.intent === 'danger' && styles.danger,
        );
        const button = (
          <Button
            key={a.key}
            appearance={appearance}
            size="small"
            icon={a.icon}
            disabled={a.disabled}
            onClick={a.onClick}
            className={className}
          >
            {a.label}
          </Button>
        );
        return a.tooltip ? (
          <Tooltip key={a.key} content={a.tooltip} relationship="description">
            {button}
          </Tooltip>
        ) : button;
      })}
    </div>
  );
}
